import { match } from 'ts-pattern'
import { z } from 'zod'
import type { AppScanWebhookAuthMethod } from '../../../utils/api-client/api-schemas.ts'
import { buildPath } from '../../../utils/build-path.ts'
import {
  type ConnectionWebhookRouteType,
  type CreatableIntegrationType,
  getHasConnectionWebhookRoute,
  getIsWebhookTool,
} from './integration-instance-utils.ts'

/**
 * The `webhooks` HAL link on a Sonar or AppScan instance, holding the absolute URL that tool must
 * call. The URL names the instance by the id the platform generates on insert, which is why it can
 * only be read back off a response and is never composed from this page's own origin.
 *
 * The contract declares this link: `SonarIntegrationLinks` (ISS-8743) and `AppScanIntegrationLinks`
 * (ISS-8749) each require a `webhooks` member, and each replaces `ToolIntegrationLinks` on its own
 * response. So a parsed `Integration` of either type always has one. This structural reader survives
 * only for the create response, which `usePostIntegration` types as `unknown` — the drawer hands the
 * URL out immediately after a save, before anything has parsed the body into an `Integration`.
 */
const WebhookLinkCarrierSchema = z
  .object({
    _links: z
      .object({
        webhooks: z.object({ href: z.string().min(1) }),
      })
      .loose(),
  })
  .loose()

/**
 * The webhook URL an instance reports, or `undefined` when it reports none. Takes an `unknown` so it
 * reads both a create response — which the mutation types that way — and a parsed `Integration`.
 */
export const getWebhookUrl = (integrationOrResponse: unknown): string | undefined => {
  const parsed = WebhookLinkCarrierSchema.safeParse(integrationOrResponse)
  return parsed.success ? parsed.data._links.webhooks.href : undefined
}

const appOrigin = () =>
  // eslint-disable-next-line no-restricted-properties -- an absolute URL for a third party to call, not navigation
  window.location.origin

/**
 * The URL a source provider's connection receives webhook deliveries at.
 *
 * Per-connection because the path is what tells Pixee which connection sent a delivery: a payload
 * names a project or an installation, not the server it came from, and the secret the delivery is
 * verified against belongs to one connection. The route names its provider before the connection
 * id; the platform exempts webhook paths from its browser login by matching patterns that end in
 * `webhooks`, so a route shaped any other way answers a delivery with a 302 to the identity provider.
 *
 * Composed rather than followed, unlike {@link getWebhookUrl}: Sonar and AppScan serve a
 * `_links.webhooks`, and these three serve none, so there is no link to follow. Nothing in this repo
 * verifies the address is the one the platform listens on, and if the route moves this keeps
 * printing the old one. ISS-8994 asks for the relation.
 */
export const getConnectionWebhookUrl = ({ type, id }: { type: ConnectionWebhookRouteType; id: string }): string =>
  `${appOrigin()}${buildPath('/api/v1/integrations/:type/:integrationId/webhooks', { type, integrationId: id })}`

const CreatedIdSchema = z.object({ id: z.string().min(1) }).loose()

export const getCreatedId = (response: unknown): string | undefined => {
  const parsed = CreatedIdSchema.safeParse(response)
  return parsed.success ? parsed.data.id : undefined
}

const WebhookUserSchema = z.object({ webhook_user: z.string().min(1) }).loose()

/**
 * The fixed user half of an Azure DevOps connection's service-hook credential. The user never types
 * it: the platform assigns it and reports it on the connection (`AzureIntegrationResponse.webhook_user`),
 * because the service hook has to present it.
 */
export const getAzureWebhookUser = (response: unknown): string | undefined => {
  const parsed = WebhookUserSchema.safeParse(response)
  return parsed.success ? parsed.data.webhook_user : undefined
}

/**
 * The chooser's own labels are `secret` and `basic`; the contract's `AppScanWebhookAuthMethod` names
 * the same two choices `shared-secret` and `basic-auth`. Translated in one place, because the
 * platform accepts a credential only when the method it belongs to is the one named on the request
 * and rejects the other one's outright.
 */
export const getWebhookAuthMethod = (choice: WebhookAuthChoice): AppScanWebhookAuthMethod =>
  match(choice)
    .with('basic', () => 'basic-auth' as const)
    .with('secret', () => 'shared-secret' as const)
    .exhaustive()

export const WEBHOOK_URL_LABEL = 'Webhook URL'
export const WEBHOOK_URL_COPY_LABEL = 'Copy webhook URL'

/** Says what deleting the instance costs, on every screen that hands the URL out. */
export const getWebhookUrlHelperText = (toolLabel: string): string =>
  `This URL is tied to this instance. If you delete the instance, the URL stops working and you will need to reconfigure ${toolLabel} with a new one.`

/**
 * What the delete confirmation says about inbound deliveries, or `undefined` for a type Pixee only
 * polls. Sits beside {@link getWebhookUrlHelperText}, which states the same fact for the two tools
 * whose URL the Setup and Manage drawers hand out with helper text attached: both exist because the
 * route names the instance by the id the platform generated for it, so the URL dies with the
 * instance and a replacement is reachable at a different one. The three providers' URLs are shown in
 * the Manage drawer without that helper text, so for them this modal is the only place it is said.
 *
 * Two copies rather than one, because the delivering side differs. A tool was pointed at Pixee to
 * send its results, and the user is told what stops arriving. A source provider delivers only if the
 * user created a hook in the provider's own product, and nothing here reports whether they did:
 * Azure's `webhook_configured` and GitHub's required `webhook_secret` say that Pixee holds a
 * credential — this side of the exchange — and GitLab reports nothing at all. So the provider copy
 * is written to hold either way, and what it promises is the part that certainly breaks: the URL.
 */
export const getWebhookDeleteWarning = ({
  type,
  toolLabel,
}: {
  type: CreatableIntegrationType
  toolLabel: string
}): string | undefined => {
  if (getIsWebhookTool(type)) {
    return `${toolLabel} is configured to send results to this instance's webhook URL. Deleting the instance stops those deliveries; to keep receiving results, connect a replacement instance and update the URL in ${toolLabel}.`
  }
  if (getHasConnectionWebhookRoute(type)) {
    return `Deleting this connection makes its webhook URL stop working, so any ${toolLabel} ${getProviderHookNoun(type)} pointing at it stops delivering. A replacement connection gets a different URL, which you have to set in ${toolLabel}.`
  }
  return undefined
}

/**
 * What the provider's own product calls the thing the user creates there. Azure DevOps has no
 * feature named "webhook" — its settings call it a service hook, the term this repo's contract also
 * uses for it (`AzureIntegrationResponse.webhook_user`: "the basic auth an Azure DevOps service hook
 * must present") — so copy that says "webhook" sends an Azure user looking for a screen that does
 * not exist. Pixee's own side keeps the one name it is labelled with, "Webhook URL".
 */
const getProviderHookNoun = (type: ConnectionWebhookRouteType): string =>
  match(type)
    .with('azure', () => 'service hook')
    .with('gitlab', 'github', () => 'webhook')
    .exhaustive()

/**
 * Shown beside a Sonar webhook URL that no secret protects. A statement of fact rather than an
 * error: the user chose this by leaving the secret blank, so nothing is marked invalid and nothing
 * is disabled. It exists so the choice is visible on the screen that hands out the URL, and not only
 * on the form where it was made.
 */
export const UNPROTECTED_WEBHOOK_HELPER_TEXT =
  'This URL is not protected — Pixee accepts any call to it. Add a webhook secret to verify that calls come from your Sonar.'

export type WebhookAuthChoice = 'secret' | 'basic'

export type WebhookHandoffSubject =
  | { type: 'sonar'; hasWebhookSecret: boolean }
  | { type: 'appscan'; webhookAuthChoice: WebhookAuthChoice }
  | { type: 'github' }
  | { type: 'gitlab' }
  | { type: 'azure'; webhookUser: string | undefined }

/**
 * What the user still has to do in the tool itself. Sonar's secret step is omitted when the instance
 * was saved without one, so the list renumbers rather than showing a step the user cannot complete —
 * and no copy invites them to set a secret in Sonar alone, which would have Sonar sign calls Pixee
 * does not check.
 */
export const getWebhookHandoffSteps = (subject: WebhookHandoffSubject): string[] =>
  match(subject)
    .with({ type: 'sonar' }, ({ hasWebhookSecret }) =>
      [
        'In SonarQube, open Administration → Configuration → Webhooks (global, or per project for SonarQube Server).',
        'Create a webhook with the URL above.',
        hasWebhookSecret ? 'Paste the webhook secret you entered into the Secret field.' : undefined,
        'Save. Results from your next analysis will appear in Pixee.',
      ].filter((step): step is string => step !== undefined)
    )
    .with({ type: 'appscan' }, ({ webhookAuthChoice }) => [
      'In AppScan, open the webhook settings for your application or organization.',
      'Set the URL to the Webhook URL above.',
      webhookAuthChoice === 'secret'
        ? "Include your webhook secret in the request body's webhook_secret field."
        : 'Use the username and password you entered.',
      'Save. Results from your next scan will appear in Pixee.',
    ])
    .with({ type: 'github' }, () => [
      'In GitHub, open Settings → Developer settings → GitHub Apps, then your App → General.',
      'Under Webhook, tick Active, set the Webhook URL to the URL above, and set the Webhook secret to the one you entered.',
      'Save changes. After the App’s next event, or after you Redeliver a recent delivery under Advanced → Recent Deliveries, the delivery should show a 200 response.',
    ])
    .with({ type: 'gitlab' }, () => [
      'In GitLab, open your project or group, then Settings → Webhooks, and add a new webhook.',
      'Set the URL to the URL above and the Secret token to the webhook secret you entered.',
      'Under Trigger, select Pipeline events, then save.',
      'Use Test → Pipeline events on the saved webhook. GitLab should report a 2xx response.',
    ])
    .with({ type: 'azure' }, ({ webhookUser }) => [
      'In Azure DevOps, open Project settings → Service hooks and create a subscription with the Web Hooks service.',
      'Choose the Build completed event.',
      webhookUser === undefined
        ? 'Set the URL to the URL above, and the basic authentication password to the webhook password you entered.'
        : `Set the URL to the URL above, the basic authentication username to ${webhookUser}, and the password to the webhook password you entered.`,
      'Use Test on the subscription. Azure DevOps should report a 2xx response.',
    ])
    .exhaustive()

// Mirrors pixee-platform's admin-console routes: `GitHubEventResource` (`/github-event`),
// `GitLabWebhooksResource` and `AzureWebhookResource` (`/{gitlab,azure}-default/webhooks`). Nothing
// here checks them, so if one moves this keeps sending admins to the old URL.
const getSystemConnectionWebhookPath = (type: ConnectionWebhookRouteType): string =>
  match(type)
    .with('github', () => '/github-event')
    .with('gitlab', () => '/api/v1/integrations/gitlab-default/webhooks')
    .with('azure', () => '/api/v1/integrations/azure-default/webhooks')
    .exhaustive()

/**
 * Shown on the handoff when the deployment also has an admin-console connection of the same
 * provider, whose deliveries arrive at a different URL from the one just handed out.
 */
export const getSystemConnectionWebhookNote = ({
  type,
  toolLabel,
}: {
  type: ConnectionWebhookRouteType
  toolLabel: string
}): string =>
  match(type)
    // Pixee refuses a second connection for the admin-console App, so this connection is always a
    // different App and the admin-console App's webhook must stay where it is.
    .with(
      'github',
      () =>
        `This deployment also has a GitHub App configured in the admin console. That App keeps delivering to ${getSystemConnectionWebhookPath(type)}; set the URL above on this connection's App only, not on the admin-console one.`
    )
    .with(
      'gitlab',
      'azure',
      () =>
        `This deployment also has a ${toolLabel} connection configured in the admin console. A ${toolLabel} ${getProviderHookNoun(type)} that still points at ${getSystemConnectionWebhookPath(type)} stops delivering once the admin-console settings are removed; point it at the URL above instead.`
    )
    .exhaustive()
